import {Livraison} from "./apiLivraisons";
import {Produit} from "./apiProduits";
import {api, ApiResponse} from "./api";
import { Utilisateur } from "./apiUtilisateurs";
import { AxiosError } from "axios";

export interface Panier {
	id: number;
	users_id: number;
	statut: string;
	created_at: string;
	updated_at: string;
	produits: Produit[];
	livraisons: Livraison[];
	total: number;
	total_commande: number;
}

export interface PanierRequest {
	statut?: string;
	produits?: Array<ProduitRequest>;
}

export interface ProduitRequest {
	produits_id: number;
	quantite: number;
}

export interface HistoriquePanier {
	id: number;
	statut: string;
	dateLivraison: string;
	referent_nom_complet: string;
	nbProduits: number;
	total: number;
	total_commande: number;
	produits: Produit[];
	adresse: string;
}

export interface PanierPayer {
	id: number;
	prixTotal: number;
	nombreProduits: number;
	produits: Produit[];
	adresseLivraison: string;
	dateLivraison: string;
	referent: Utilisateur;
}

export interface PanierEnCours {
	panier: Panier;
	produits_retires: StockModifie[];
	produits_ajustes: StockModifie[];
}

export interface StockModifie {
	nom: string;
	produit_id: number;
	quantite_demandee: number;
}

/** Get a panier by id */
export const getPanier = async (id: number): Promise<Panier> => {
	try {
		const response = await api.get<ApiResponse<Panier>>(`/paniers/${id}`);
			return response.data.data;
	} catch (error) {
		console.error('Erreur lors de la récupération des paniers:', error);
		throw error;
	}
}

/** si admin => tous, sinon uniquement ceux de l'utilisateur connecté */
export const getPaniers = async (): Promise<Panier[]> => {
	try {
		const response = await api.get<ApiResponse<Panier[]>>('/paniers');
		return response.data.data.map((panier) => ({
			...panier,
		}));
	} catch (error) {
		console.error('Erreur lors de la récupération des paniers:', error);
		throw error;
	}
}

/** get paniers en cours */
export const getPanierEnCours = async (): Promise<PanierEnCours> => {
	try {
		const response = await api.get<ApiResponse<PanierEnCours>>('/paniers/en_cours');
		return {
			panier: response.data.data as unknown as Panier,
			produits_retires: response.data.metadata?.produits_retires ?? [],
			produits_ajustes: response.data.metadata?.produits_ajustes ?? [],
		};
	} catch (error) {
		console.error('Erreur lors de la récupération des paniers:', error);
		throw error;
	}
}

/** get paniers historique */
export const getPaniersHistorique = async (): Promise<HistoriquePanier[]> => {
		try {
			const response = await api.get<ApiResponse<Panier[]>>('/paniers/historique');
			return response.data.data.map((panier) => ({
			  ...panier,
				statut:panier.livraisons[0]?.statut ?? null,
				dateLivraison: panier.livraisons[0]?.date_livraison ? new Date(panier.livraisons[0].date_livraison).toLocaleDateString('fr-FR') : 'A définir',
				referent_nom_complet:panier.livraisons[0]?.referent?.nom_complet ?? 'A définir',
				nbProduits:panier.produits.length,
				adresse:panier.livraisons[0]?.adresse ?? 'A définir',
			}));
		} catch (error) {
			console.error('Erreur lors de la récupération des paniers:', error);
			throw error;
		}
}

/** create panier en cours */
export const createPanierEnCours = async (data: PanierRequest): Promise<Panier> => {
	try {
		const response = await api.post<ApiResponse<Panier>>('/paniers', data);
		return response.data.data;
	} catch (error) {
		console.error('Erreur lors de la création du panier en cours:', error);
		throw error;
	}
}

/** update a panier */
export const updatePanier = async (id: number, panier: Panier): Promise<Panier> => {
	try {
		const response = await api.put<ApiResponse<Panier>>(`/paniers/${id}`, panier);
		return response.data.data;
	} catch (error) {
		console.error('Erreur lors de la mise à jour du panier:', error);
		throw error;
	}
}

/** update panier en cours */
export const updatePanierEnCours = async (data: PanierRequest): Promise<Panier | null> => {
	try {
		const response = await api.put<ApiResponse<Panier>>('/paniers', data);
		return response.data.data;
	} catch (error) {
		if (error instanceof AxiosError && error.response?.status === 422) {
			window.location.reload();
			return null;
		} else {
			console.error('Erreur lors de la mise à jour du panier en cours:', error);
			throw error;
		}
	}
}

/** paiement d'un panier */
export const paiement = async (data: Panier): Promise<ApiResponse<any>> => {
	try {
		const response = await api.post<ApiResponse<any>>('/paniers/payer', data);
		return response.data;
	} catch (error) {
		console.error('Erreur lors du paiement du panier:', error);
		throw error;
	}
}

export const getPanierPayer = async (paymentIntentId: string): Promise<PanierPayer> => {
	try {
		const response = await api.get<ApiResponse<PanierPayer>>(`/paniers/paymentIntent/${paymentIntentId}`);
		return response.data.data;
	} catch (error) {
		console.error('Erreur lors de la récupération du panier par payment intent id:', error);
		throw error;
	}
}